'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ShoppingCart, LogIn, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { AccountDrawer, getAccountInitials } from '@/components/layout/account-drawer';
import { useSessionSummary } from '@/hooks/useSessionSummary';
import { useOrderStore } from '@/store/order-store';
import { cn } from '@/lib/utils';
import Image from 'next/image';

interface HeaderProps {
  onOpenCart?: () => void;
}

export function Header({ onOpenCart }: HeaderProps) {
  const [cartCount, setCartCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const pathname = usePathname();
  const items = useOrderStore((state) => state.items);
  const totalItems = useOrderStore((state) => state.totalItems);
  const { summary } = useSessionSummary();

  useEffect(() => {
    setCartCount(totalItems());
  }, [items, totalItems]);

  const navLinks = [
    { href: '/', label: 'الرئيسية' },
    { href: '/products?brand=samsung', label: 'الهواتف' },
    { href: '/products', label: 'جميع المنتجات' },
    { href: '/devices', label: 'الموديلات' },
  ];

  const displayName = summary?.fullName?.trim() || summary?.email.split('@')[0] || '';
  const initials = getAccountInitials(summary?.fullName, summary?.email ?? '');

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/90 backdrop-blur-xl">
      <div className="container mx-auto flex h-18 items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={onOpenCart} className="gap-2.5">
            {cartCount > 0 ? (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground">
                {cartCount}
              </span>
            ) : null}
            <span className="hidden text-xs font-semibold sm:inline">قائمة الطلبات</span>
            <ShoppingCart />
          </Button>
        </div>

        <NavigationMenu className="hidden md:flex" align="center">
          <NavigationMenuList className="gap-1">
            {navLinks.map((link) => {
              const isActive =
                link.href === '/'
                  ? pathname === '/'
                  : pathname.startsWith(link.href.split('?')[0]);
              return (
                <NavigationMenuItem key={link.label}>
                  <NavigationMenuLink
                    render={<Link href={link.href} />}
                    className={cn(
                      navigationMenuTriggerStyle(),
                      'text-sm font-bold',
                      isActive ? 'bg-muted text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {link.label}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              );
            })}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex items-center gap-3">
          <Link href="/" className="flex items-center gap-2.5 group">
            <span className="text-xl font-extrabold tracking-tight transition-colors group-hover:text-primary">
              <Image 
              src={"/valleytech.svg"}
              alt="ValleyTech"
              width={150}
              height={100}
              style={{ width: "auto", height: "auto" }}
              />
            </span>
          </Link>

          <ThemeToggle />

          {summary ? (
            <Link
              href="/profile"
              aria-label="الملف الشخصي"
              className="hidden min-w-0 items-center gap-2.5 rounded-xl border border-transparent px-2 py-1.5 transition-colors hover:bg-muted md:flex"
            >
              <span
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground"
              >
                {initials}
              </span>
              <span className="min-w-0 text-start">
                <span className="block max-w-32 truncate text-xs font-extrabold">{displayName}</span>
                <span className="block max-w-32 truncate text-[11px] font-semibold text-muted-foreground" dir="ltr">
                  {summary.email}
                </span>
              </span>
            </Link>
          ) : (
            <Button variant="outline" className="hidden gap-2 md:flex" render={<Link href="/login" />}>
              <LogIn />
              <span className="text-xs font-bold">تسجيل الدخول</span>
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="الحساب"
            onClick={() => setIsDrawerOpen(true)}
          >
            {summary ? (
              <span
                aria-hidden
                className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground"
              >
                {initials}
              </span>
            ) : (
              <UserRound />
            )}
          </Button>
        </div>
      </div>
      <AccountDrawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen} onCartNavigate={onOpenCart} />
    </header>
  );
}
